import { useLocalization } from "@fluent/react";
import { formatDate } from "@lending/utils/formatters";
import styles from "../PersonalInfoReview.module.css";
import { formatPlaidAddress, maskSsn } from "../utils";
import { StatusInfoRow } from "./StatusInfoRow";
import type { PersonalInfoReviewFormData } from "./PersonalInfoEditForm";
import type { FieldErrors } from "react-hook-form";

interface PersonalInfoSummaryProps {
  formValues: Partial<PersonalInfoReviewFormData>;
  errors: FieldErrors<PersonalInfoReviewFormData>;
  isEditing: boolean;
}

function PersonalInfoSummary({ formValues, errors, isEditing }: PersonalInfoSummaryProps) {
  const { l10n } = useLocalization();

  const nameHasError = !!errors.first_name || !!errors.last_name;
  const ssnHasError = !!errors.tax_id;
  const dobHasError = !!errors.date_of_birth;
  const addressHasError =
    !!errors.street || !!errors.city || !!errors.region || !!errors.postal_code;

  const nameValue = [formValues.first_name, formValues.last_name].filter(Boolean).join(" ");
  const ssnValue = formValues.tax_id ? maskSsn(formValues.tax_id.slice(-4)) : "";
  const dobValue =
    formValues.date_of_birth && formValues.date_of_birth.length >= 10
      ? formatDate(formValues.date_of_birth)
      : "";
  const addressValue = formatPlaidAddress({
    street: formValues.street ?? null,
    street2: formValues.street2 ?? null,
    city: formValues.city ?? null,
    region: formValues.region ?? null,
    postal_code: formValues.postal_code ?? null,
  });

  const missing = l10n.getString("plaid-pii-review-missing-info", null, "Missing");

  return (
    <div className={`${styles.infoRows} ${styles.infoRowsLayer} ${isEditing ? styles.hidden : ""}`}>
      <StatusInfoRow
        label={l10n.getString("confirm-name-label", null, "Name")}
        value={nameHasError ? missing : nameValue}
        status={nameHasError ? "error" : "success"}
      />
      <StatusInfoRow
        label={l10n.getString("confirm-ssn-label", null, "SSN")}
        value={ssnHasError ? missing : ssnValue}
        status={ssnHasError ? "error" : "success"}
      />
      <StatusInfoRow
        label={l10n.getString("confirm-dob-label", null, "Date of birth")}
        value={dobHasError ? missing : dobValue}
        status={dobHasError ? "error" : "success"}
      />
      <StatusInfoRow
        label={l10n.getString("confirm-address-label", null, "Address")}
        value={addressValue || missing}
        status={addressHasError ? "error" : "success"}
      />
    </div>
  );
}

export { PersonalInfoSummary };
